// [xleaders] XO-07 — một chỗ duy nhất dịch kết quả của
// `ZaloPersonal::StartConversationService` thành thông báo cho sale. Có hai lối vào gọi cùng
// endpoint này (nút ở hồ sơ khách gửi `contact_id`, hộp thoại ở đầu danh sách hội thoại gửi
// `phone`); nhân đôi bảng mã lỗi giữa hai component là chắc chắn lệch nhau khi service thêm mã mới.
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import zaloPersonalChannel from 'dashboard/api/channel/zaloPersonalChannel';

const SUCCESS_KEYS = {
  friend: 'CONTACT_PANEL.ZALO_FRIEND.ALREADY_FRIEND',
  request_sent: 'CONTACT_PANEL.ZALO_FRIEND.REQUEST_SENT',
};

// Đủ tám mã service có thể trả về. Thiếu một mã là sale nhận thông báo chung chung, không biết
// phải làm gì tiếp.
const ERROR_KEYS = {
  not_on_zalo: 'CONTACT_PANEL.ZALO_FRIEND.NOT_ON_ZALO',
  zalo_account_banned: 'CONTACT_PANEL.ZALO_FRIEND.ACCOUNT_BANNED',
  blocked: 'CONTACT_PANEL.ZALO_FRIEND.BLOCKED',
  quota_exceeded: 'CONTACT_PANEL.ZALO_FRIEND.QUOTA_EXCEEDED',
  channel_disconnected: 'CONTACT_PANEL.ZALO_FRIEND.CHANNEL_DISCONNECTED',
  phone_required: 'CONTACT_PANEL.ZALO_FRIEND.PHONE_REQUIRED',
  request_in_flight: 'CONTACT_PANEL.ZALO_FRIEND.REQUEST_IN_FLIGHT',
  unexpected_error: 'CONTACT_PANEL.ZALO_FRIEND.UNEXPECTED_ERROR',
};

/**
 * @param {Object} [options]
 * @param {string} [options.phoneRequiredKey] - `phone_required` nghĩa khác nhau tuỳ lối vào: ở panel
 *   khách là "khách chưa có số", ở hộp thoại gõ tay là "số vừa gõ không dùng được". Cho caller đổi
 *   đúng một thông báo, KHÔNG tách đôi cả bảng mã.
 */
export function useZaloFriendRequest({
  phoneRequiredKey = ERROR_KEYS.phone_required,
} = {}) {
  const { t } = useI18n();
  const isSending = ref(false);
  const errorKeys = { ...ERROR_KEYS, phone_required: phoneRequiredKey };

  /**
   * @param {number|string} inboxId - inbox Zalo cá nhân gửi lời mời
   * @param {Object} payload - `{ contact_id }` hoặc `{ phone }`
   * @returns {Promise<Object|null>} body của response khi thành công, `null` khi thất bại
   */
  const sendFriendRequest = async (inboxId, payload) => {
    isSending.value = true;
    try {
      const { data } = await zaloPersonalChannel.startConversation(
        inboxId,
        payload
      );
      useAlert(
        t(SUCCESS_KEYS[data.friend_status] || SUCCESS_KEYS.request_sent)
      );
      return data;
    } catch (error) {
      // Controller trả { error: '<mã>' } kèm 422, nhưng axios chỉ để lại "Request failed with
      // status code 422" ở error.message — phải bóc từ response mới ra lý do thật.
      const code = error?.response?.data?.error;
      useAlert(t(errorKeys[code] || 'CONTACT_PANEL.ZALO_FRIEND.FAILED'));
      return null;
    } finally {
      isSending.value = false;
    }
  };

  return { isSending, sendFriendRequest };
}
